import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Loader2, CheckCircle2, X } from 'lucide-react';
import SelectCheckOptionsModal from './SelectCheckOptionsModal';
import NewBackgroundCheckModal from './NewBackgroundCheckModal';
import PaymentModal from './PaymentModal';
import BulkSubjectsModal from './BulkSubjectsModal';
import AuthRequiredModal from './AuthRequiredModal';
import ChooseSubjectCountModal from './ChooseSubjectCountModal';
import { Button } from '../ui/button';
import { CheckType } from '../../types';
import type { BatchSubject } from '../../types';
import { api, useCreateInvestigationMutation, useCreateInvestigationBatchMutation } from '../../store/api/apiSlice';
import { clearSelection, readSelection } from '../../utils/planSelection';
import { clearDraft, readDraft, saveDraft, type DraftSubject } from '../../utils/investigationDraft';
import { useAppSelector } from '../../hooks/useAppStore';
import { useAppDispatch } from '../../hooks/useAppStore';

interface InvestigationFlowProps {
    isOpen: boolean;
    onClose: () => void;
}

type FlowStep =
    | 'package'
    // Reached when an anonymous visitor tries to submit. Their work is parked
    // in a draft first, so signing up never costs them what they typed.
    | 'authRequired'
    // How many people this purchase covers. Asked after the plan, because the
    // plan is what a batch shares.
    | 'howMany'
    | 'details'
    | 'bulkDetails'
    | 'payment'
    | 'complimentary'
    | 'closed';

const getReadableError = (err: any, fallback = 'Failed to create investigation') => {
    const serverMessage = err?.data?.message ?? err?.message ?? err;
    if (Array.isArray(serverMessage)) return serverMessage.join(', ');
    if (typeof serverMessage === 'string') return serverMessage;
    if (serverMessage && typeof serverMessage === 'object') {
        return serverMessage.message || serverMessage.error || JSON.stringify(serverMessage);
    }
    return fallback;
};

export default function InvestigationFlow({ isOpen, onClose }: InvestigationFlowProps) {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    // Anonymous visitors are allowed all the way to the point of submission.
    const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

    /**
     * Read at call time, not from the render closure.
     *
     * Inline login stores the token and then resumes the submission in the same
     * tick, so the `isAuthenticated` captured by these handlers is still false
     * at that moment. Checking the stored token as well means a just-signed-in
     * visitor carries on instead of being bounced back to the gate.
     */
    const hasSession = () => isAuthenticated || !!localStorage.getItem('access_token');
    const [currentStep, setCurrentStep] = useState<FlowStep>('package');
    const [checkType, setCheckType] = useState<CheckType>(CheckType.STANDARD);
    // What the employer chose on the package card: the plan features they kept
    // (a short list means they cherry picked) and any extras on top.
    const [features, setFeatures] = useState<string[]>([]);
    const [addons, setAddons] = useState<string[]>([]);
    const [investigationId, setInvestigationId] = useState<string>('');
    // Set instead of investigationId when several people were bought together;
    // the two are mutually exclusive all the way to the payment call.
    const [batchId, setBatchId] = useState<string>('');
    const [batchCount, setBatchCount] = useState(0);
    const [planName, setPlanName] = useState<string>('');
    const [pricePerSubject, setPricePerSubject] = useState(0);
    // Held so the flow can submit straight away once the visitor is back from
    // signing in, rather than making them retype anything.
    const [pendingSubjects, setPendingSubjects] = useState<DraftSubject[]>([]);
    // The single-person payload that hit the auth gate, documents and all.
    // Inline login keeps this component mounted, so the File objects survive
    // and the submission can resume with the exact same attachments.
    const [pendingDetails, setPendingDetails] = useState<any>(null);
    const [complimentaryEmail, setComplimentaryEmail] = useState<string>('');
    const [creationError, setCreationError] = useState<string>('');
    const [isCreating, setIsCreating] = useState(false);

    // Pick a parked draft back up. Runs when the flow opens for someone who is
    // now signed in, which is exactly the return leg of the auth detour.
    useEffect(() => {
        if (!isOpen) return;

        const draft = isAuthenticated ? readDraft() : null;
        if (!draft) {
            // No draft, but they may have already chosen a plan on the pricing
            // page. Skip the picker rather than asking the same question twice.
            const chosen = readSelection();
            if (chosen?.planName && typeof chosen.pricePerSubject === 'number') {
                setCheckType(chosen.checkType as CheckType);
                setFeatures(chosen.features);
                setAddons(chosen.addons);
                setPlanName(chosen.planName);
                setPricePerSubject(chosen.pricePerSubject);
                // Consumed: reopening later should start from the picker again
                // rather than silently reusing a stale choice.
                clearSelection();
                setCurrentStep('howMany');
            }
            return;
        }

        setCheckType((draft.checkType ?? CheckType.STANDARD) as CheckType);
        setFeatures(draft.features);
        setAddons(draft.addons);
        setPlanName(draft.planName);
        setPricePerSubject(draft.pricePerSubject);
        setPendingSubjects(draft.subjects);
        // Land them back on the step they were on, with their details filled in,
        // so they confirm and pay rather than starting over.
        setCurrentStep(draft.mode === 'bulk' ? 'bulkDetails' : 'details');
    }, [isOpen, isAuthenticated]);

    const [createInvestigation] = useCreateInvestigationMutation();
    const [createInvestigationBatch] = useCreateInvestigationBatchMutation();

    // Free/exempt investigations are settled without a gateway redirect, so the
    // Investigations list won't refetch on its own — force it here.
    const showComplimentary = (email: string) => {
        dispatch(api.util.invalidateTags(['Investigation', 'Transaction', 'Analytics']));
        setComplimentaryEmail(email);
        setCurrentStep('complimentary');
    };

    const handleSelectPackage = (
        packageId: string,
        selectedFeatures: string[],
        selectedAddons: string[],
        meta: { planName: string; pricePerSubject: number },
    ) => {
        const typeMap: Record<string, CheckType> = {
            standard: CheckType.STANDARD,
            enhanced: CheckType.ENHANCED,
            pro: CheckType.PRO,
            Standard: CheckType.STANDARD,
            Enhanced: CheckType.ENHANCED,
            Pro: CheckType.PRO,
        };
        // Known tiers map to the enum; any new backend package flows through with
        // its own id as the checkType (the create/payment API takes it as a string),
        // so new plans work without a frontend change.
        setCheckType((typeMap[packageId] ?? packageId) as CheckType);
        setFeatures(selectedFeatures);
        setAddons(selectedAddons);
        // Their pre-signup choice has now been carried into the flow, so the
        // stash has done its job and should not resurface on a later visit.
        clearSelection();
        setPlanName(meta.planName);
        setPricePerSubject(meta.pricePerSubject);
        setCurrentStep('howMany');
    };

    /**
     * Signed in without leaving the flow — carry straight on with whatever was
     * blocked, rather than making them press the button a second time.
     */
    const handleAuthenticated = () => {
        if (pendingDetails) {
            void handleDetailsNext(pendingDetails);
        } else if (pendingSubjects.length > 0) {
            void handleBulkNext(pendingSubjects as BatchSubject[]);
        }
    };

    const handleChooseCount = (mode: 'single' | 'bulk') => {
        setCreationError('');
        setCurrentStep(mode === 'bulk' ? 'bulkDetails' : 'details');
    };

    /**
     * Create every investigation in the batch, then pay once.
     *
     * The batch is created first and paid for second, matching the single flow.
     * The server refuses to create a partial batch, so a failure here leaves
     * nothing behind and the employer can fix the offending row and resubmit.
     */
    const handleBulkNext = async (subjects: BatchSubject[]) => {
        setCreationError('');

        // Anonymous: park everything and ask them to sign in. Nothing is sent
        // to the server, so no half-made investigation is left behind.
        if (!hasSession()) {
            saveDraft({
                checkType,
                features,
                addons,
                planName,
                pricePerSubject,
                mode: 'bulk',
                subjects: subjects as DraftSubject[],
            });
            setPendingSubjects(subjects as DraftSubject[]);
            setPendingDetails(null);
            setCurrentStep('authRequired');
            return;
        }

        setIsCreating(true);

        try {
            const result = await createInvestigationBatch({
                checkType,
                subjects,
                ...(features.length > 0 && { features }),
                ...(addons.length > 0 && { addons }),
            });

            if ('error' in result) {
                setCreationError(getReadableError(result.error, 'Failed to create the batch'));
                setIsCreating(false);
                return;
            }

            const data = result.data;
            if (!data?.batchId) {
                setCreationError('The batch was created but no id came back. Please contact support.');
                setIsCreating(false);
                return;
            }

            // Created for real, so the parked copy has served its purpose.
            clearDraft();
            setBatchId(data.batchId);
            setBatchCount(data.subjectCount);
            setIsCreating(false);

            // Payment-exempt employers: the server already marked the batch paid
            // and invited every employee, so there is nothing to check out.
            if (data.isComplimentary || data.isPaid) {
                showComplimentary('');
            } else {
                setCurrentStep('payment');
            }
        } catch (err) {
            setCreationError(getReadableError(err, 'Failed to create the batch'));
            setIsCreating(false);
        }
    };

    const handleDetailsNext = async (details: any) => {
        setCreationError('');

        // Anonymous: park it and ask them to sign in. Documents are the one
        // thing a draft cannot carry (File objects do not survive storage). If
        // they sign in inline the flow stays mounted and the originals are
        // reused; only the navigate-away route loses them.
        if (!hasSession()) {
            saveDraft({
                checkType,
                features,
                addons,
                planName,
                pricePerSubject,
                mode: 'single',
                subjects: [
                    {
                        employeeFirstName: details.employeeFirstName?.trim() ?? '',
                        employeeMiddleName: details.employeeMiddleName?.trim() || undefined,
                        employeeLastName: details.employeeLastName?.trim() ?? '',
                        employeeEmail: details.employeeEmail?.trim() ?? '',
                        employeePhone: details.employeePhone ?? '',
                        employeeGender: details.employeeGender || undefined,
                    },
                ],
            });
            setPendingDetails(details);
            setPendingSubjects([]);
            setCurrentStep('authRequired');
            return;
        }

        setIsCreating(true);

        try {
            const investigationData = {
                checkType,
                employeeFirstName: details.employeeFirstName.trim(),
                ...(details.employeeMiddleName && { employeeMiddleName: details.employeeMiddleName.trim() }),
                employeeLastName: details.employeeLastName.trim(),
                employeeEmail: details.employeeEmail.trim(),
                employeePhone: details.employeePhone,
                ...(details.employeeGender && { employeeGender: details.employeeGender }),
                ...(details.investigationCategory && { investigationCategory: details.investigationCategory }),
                ...(features.length > 0 && { features }),
                ...(addons.length > 0 && { addons }),
            };

            const result = await createInvestigation({
                data: investigationData,
                documents: details.documents,
                documentTypes: details.documentTypes,
            });

            if ('error' in result) {
                setCreationError(getReadableError(result.error));
                setIsCreating(false);
                return;
            }

            const id = result.data?.id;
            if (!id) {
                setCreationError('Investigation was created but no ID was returned. Please contact support.');
                setIsCreating(false);
                return;
            }

            clearDraft();
            setInvestigationId(id);
            setIsCreating(false);

            // Payment-exempt (complimentary) accounts: the backend created the
            // investigation as paid and invited the employee — skip checkout entirely.
            // `details.paymentExempt` comes from the validate step (authoritative);
            // the create-response flags are a fallback.
            const isComplimentary =
                details.paymentExempt === true || result.data?.isComplimentary === true || result.data?.isPaid === true;

            if (isComplimentary) {
                showComplimentary(details.employeeEmail?.trim() ?? '');
            } else {
                setCurrentStep('payment');
            }
        } catch (err) {
            setCreationError(getReadableError(err));
            setIsCreating(false);
        }
    };

    const handleBack = () => {
        if (currentStep === 'payment') {
            // Back from a batch payment returns to the list of people, not to
            // the single-person form.
            setCurrentStep(batchId ? 'bulkDetails' : 'details');
        } else if (currentStep === 'bulkDetails' || currentStep === 'details') {
            setCurrentStep('howMany');
        } else if (currentStep === 'howMany') {
            setCurrentStep('package');
        }
    };

    const handleClose = () => {
        setCurrentStep('package');
        setCheckType(CheckType.STANDARD);
        setFeatures([]);
        setAddons([]);
        setInvestigationId('');
        setBatchId('');
        setBatchCount(0);
        setPlanName('');
        setPricePerSubject(0);
        setComplimentaryEmail('');
        setCreationError('');
        setIsCreating(false);
        onClose();
    };

    const handleComplimentaryDone = () => {
        handleClose();
        navigate('/investigations');
    };

    if (!isOpen) return null;

    return (
        <>
            <SelectCheckOptionsModal
                isOpen={currentStep === 'package'}
                onClose={handleClose}
                onSelectPackage={handleSelectPackage}
            />

            <AuthRequiredModal
                isOpen={currentStep === 'authRequired'}
                onClose={handleClose}
                onBack={() => setCurrentStep(pendingSubjects.length > 1 ? 'bulkDetails' : 'details')}
                onSignUp={() => navigate('/signup')}
                onAuthenticated={handleAuthenticated}
                planName={planName}
                subjectCount={Math.max(pendingSubjects.length, 1)}
                total={pricePerSubject * Math.max(pendingSubjects.length, 1)}
            />

            <ChooseSubjectCountModal
                isOpen={currentStep === 'howMany'}
                onClose={handleClose}
                onBack={handleBack}
                onChoose={handleChooseCount}
                planName={planName}
            />

            <BulkSubjectsModal
                isOpen={currentStep === 'bulkDetails'}
                onClose={handleClose}
                onBack={handleBack}
                onNext={handleBulkNext}
                checkType={checkType}
                planName={planName}
                pricePerSubject={pricePerSubject}
                externalLoading={isCreating}
                externalError={creationError}
                {...(pendingSubjects.length > 0 ? { initialSubjects: pendingSubjects } : {})}
            />

            <NewBackgroundCheckModal
                isOpen={currentStep === 'details'}
                onClose={handleClose}
                onNext={handleDetailsNext}
                checkType={checkType}
                externalLoading={isCreating}
                externalError={creationError}
            />

            {currentStep === 'payment' && (investigationId || batchId) && (
                <PaymentModal
                    isOpen
                    onClose={handleClose}
                    onBack={handleBack}
                    checkType={checkType}
                    {...(batchId ? { batchId, subjectCount: batchCount } : { investigationId })}
                    features={features}
                    addons={addons}
                    onFreeSuccess={() => showComplimentary('')}
                />
            )}

            {/* Payment-exempt success — presented as a normal successful checkout */}
            {currentStep === 'complimentary' && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 text-center">
                        <button
                            onClick={handleClose}
                            className="absolute right-4 top-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
                        >
                            <X className="w-4 h-4 text-gray-500" />
                        </button>
                        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-50">
                            <CheckCircle2 className="h-7 w-7 text-green-600" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Payment processed successfully</h3>
                        <p className="mt-2 text-sm text-gray-600">
                            {batchCount > 1
                                ? `Consent invitations have been sent to all ${batchCount} people.`
                                : complimentaryEmail
                                  ? `The consent invitation has been sent to the employee email ${complimentaryEmail}.`
                                  : 'The consent invitation has been sent to the employee email.'}
                        </p>
                        <Button
                            onClick={handleComplimentaryDone}
                            className="mt-6 w-full rounded-xl bg-gray-900 py-3 text-sm font-medium text-white hover:bg-gray-800"
                        >
                            Proceed
                        </Button>
                    </div>
                </div>
            )}

            {/* Inline overlay during investigation creation (covers transition from details → payment) */}
            {isCreating && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
                    <div className="flex items-center gap-3 rounded-xl bg-white px-6 py-4 shadow-lg">
                        <Loader2 className="h-5 w-5 animate-spin text-gray-700" />
                        <span className="text-sm font-medium text-gray-900">Creating investigation...</span>
                    </div>
                </div>
            )}
        </>
    );
}
